import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  getDefaultRouteForUser,
  getInvitedCreatorPath,
  getOnboardingStatus,
  ONBOARDING_STATUS,
} from '../../utils/onboardingStatus';

const VerificationGuard = ({
  children,
  requireApproved = true,
  allowInvited = false,
  allowedRoles = null,
}) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userType = currentUser.user_type;

  // Inferred or explicit role restriction:
  // - /creator/* routes are restricted to creators
  // - /business/* routes are restricted to business
  const effectiveAllowedRoles = allowedRoles || (
    location.pathname.startsWith('/creator/') ? ['creator'] :
    location.pathname.startsWith('/business/') ? ['business'] :
    null
  );

  if (effectiveAllowedRoles && !currentUser.is_staff && !currentUser.is_superuser && !effectiveAllowedRoles.includes(userType)) {
    // If a business user attempts to access an invited creator route with a collab ID, redirect to their business collab journey
    if (userType === 'business' && location.pathname.startsWith('/creator/invited/')) {
      const match = location.pathname.match(/^\/creator\/invited\/([^/]+)/);
      if (match && match[1]) {
        return <Navigate to={`/business/collabs/${match[1]}/journey`} replace />;
      }
    }
    return <Navigate to={getDefaultRouteForUser(currentUser)} replace />;
  }

  if (currentUser.is_staff || currentUser.is_superuser || !requireApproved) {
    return children;
  }

  const status = getOnboardingStatus(currentUser);

  if (userType === 'business') {
    if (status === ONBOARDING_STATUS.APPROVED) return children;
    if (status === ONBOARDING_STATUS.PENDING_ADMIN || status === ONBOARDING_STATUS.REJECTED) {
      return <Navigate to="/business/pending-approval" replace />;
    }
    return <Navigate to={getDefaultRouteForUser(currentUser)} replace />;
  }

  if (userType === 'creator') {
    const profile = currentUser.creator_profile;
    if (!profile) return <Navigate to="/complete-profile" replace />;

    if (status === ONBOARDING_STATUS.APPROVED) return children;

    if (status === ONBOARDING_STATUS.INVITED_ACCESS) {
      if (allowInvited) return children;
      return <Navigate to={getInvitedCreatorPath(currentUser)} replace />;
    }

    if (status === ONBOARDING_STATUS.PENDING_ADMIN || status === ONBOARDING_STATUS.REJECTED) {
      if (location.pathname.startsWith('/creator/pending-approval')) return children;
      return <Navigate to="/creator/pending-approval" replace />;
    }

    if (location.pathname.startsWith('/complete-profile')) return children;
    return <Navigate to={getDefaultRouteForUser(currentUser)} replace />;
  }

  return <Navigate to={getDefaultRouteForUser(currentUser)} replace />;
};

export default VerificationGuard;
